import React, { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { AlertTriangle, Check, MapPin } from "lucide-react";
import { PHONE_PREFIXES, splitPhone, composePhone, emailCheck } from "../lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Telefono: prefisso internazionale (default +39) + numero; value/onChange in formato E.164
export function PhoneField({ value, onChange, className = "", selectClassName = "", testPrefix = "phone", required }) {
  const { prefix, number } = splitPhone(value);
  const [pfx, setPfx] = useState(prefix || "+39");
  return (
    <div className="flex gap-2">
      <select value={pfx} onChange={(e) => { setPfx(e.target.value); onChange(composePhone(e.target.value, number)); }} data-testid={`${testPrefix}-prefix`}
        className={`h-11 rounded-md border px-2 text-sm w-[128px] shrink-0 ${selectClassName}`}>
        {PHONE_PREFIXES.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
      </select>
      <Input data-testid={`${testPrefix}-number`} required={required} inputMode="tel" value={number} placeholder="333 1234567" className={`flex-1 ${className}`}
        onChange={(e) => onChange(composePhone(pfx, e.target.value))} />
    </div>
  );
}

// Email con controllo formato e suggerimento "Intendevi…?" per i refusi dei provider
export function EmailField({ value, onChange, className = "", testPrefix = "email", required, light }) {
  const chk = emailCheck(value);
  return (
    <div>
      <Input data-testid={`${testPrefix}-input`} type="text" inputMode="email" autoCapitalize="off" required={required} value={value} placeholder="nome@dominio.it"
        className={`${className} ${!chk.valid ? "border-red-400" : ""}`} onChange={(e) => onChange(e.target.value)} />
      {!chk.valid && (
        <div className={`text-[11px] mt-1 flex flex-wrap items-center gap-1 ${light ? "text-red-600" : "text-red-300"}`} data-testid={`${testPrefix}-error`}>
          <AlertTriangle className="h-3 w-3" /> {chk.reason}
          {chk.suggestion && <button type="button" className="underline font-semibold" onClick={() => onChange(chk.suggestion)} data-testid={`${testPrefix}-suggestion`}>Intendevi {chk.suggestion}?</button>}
        </div>
      )}
    </div>
  );
}

// Verifica città/indirizzo su OpenStreetMap: propone la forma normalizzata, l'operatore conferma o mantiene
export function AddressCheck({ form, onApply, light, testPrefix = "addr" }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = `${form.address}|${form.zip_code}|${form.city}|${form.province}`;
  const [checkedKey, setCheckedKey] = useState("");
  useEffect(() => { if (res && key !== checkedKey) setRes(null); }, [key]); // eslint-disable-line
  const run = async () => {
    if (!form.city && !form.address) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/validate/address`, { address: form.address, zip_code: form.zip_code, city: form.city, province: form.province });
      setRes(data); setCheckedKey(key);
    } catch { setRes(null); } finally { setBusy(false); }
  };
  const apply = () => {
    const next = { ...form, ...res.suggestion };
    setCheckedKey(`${next.address}|${next.zip_code}|${next.city}|${next.province}`);
    onApply(res.suggestion); setRes({ ...res, changed: [] });
  };
  const box = light ? "bg-sky-50 border-sky-200 text-zinc-800" : "bg-zinc-950 border-zinc-800 text-zinc-200";
  const ok = res?.found && !(res.changed || []).length;
  return (
    <div className="mt-2 space-y-2">
      <button type="button" onClick={run} disabled={busy || (!form.city && !form.address)} data-testid={`${testPrefix}-check`}
        className={`text-xs font-semibold inline-flex items-center gap-1 ${light ? "text-sky-700" : "text-amber-300"} disabled:opacity-40`}>
        <MapPin className="h-3.5 w-3.5" /> {busy ? "Verifica in corso…" : "Verifica città e indirizzo su OpenStreetMap"}
      </button>
      {res && !res.found && <div className={`text-xs rounded-lg border p-2 ${light ? "bg-red-50 border-red-200 text-red-700" : "bg-red-500/10 border-red-500/30 text-red-300"}`} data-testid={`${testPrefix}-notfound`}><AlertTriangle className="h-3.5 w-3.5 inline mr-1" />{res.reason}</div>}
      {ok && <div className={`text-xs rounded-lg border p-2 ${light ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"}`} data-testid={`${testPrefix}-ok`}><Check className="h-3.5 w-3.5 inline mr-1" />Indirizzo verificato: {res.display_name}</div>}
      {res?.found && !ok && (
        <div className={`text-xs rounded-lg border p-3 space-y-2 ${box}`} data-testid={`${testPrefix}-suggestion`}>
          <div className="font-semibold">Forma ufficiale trovata{res.street_found === false ? " (via non riconosciuta: controlla)" : ""}:</div>
          <div>{[res.suggestion.address, res.suggestion.zip_code, res.suggestion.city, res.suggestion.province && `(${res.suggestion.province})`].filter(Boolean).join(", ")}</div>
          <div className="text-[11px] opacity-70">Campi diversi da quanto inserito: {res.changed.map((k) => ({ address: "indirizzo", zip_code: "CAP", city: "città", province: "provincia" }[k])).join(", ")}</div>
          <div className="flex gap-2">
            <button type="button" onClick={apply} data-testid={`${testPrefix}-apply`} className={`px-3 h-8 rounded-full text-xs font-semibold ${light ? "bg-sky-600 text-white" : "bg-amber-500 text-zinc-950"}`}>Applica correzione</button>
            <button type="button" onClick={() => setRes(null)} data-testid={`${testPrefix}-keep`} className={`px-3 h-8 rounded-full text-xs border ${light ? "border-zinc-300" : "border-zinc-700"}`}>Mantieni come scritto</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Consenso con doppia scelta SI / NO obbligatoria (value null = non risposto); required = solo SI ammesso
export function ConsentYesNo({ testId, title, desc, value, onChange, required = false }) {
  const answered = value === true || value === false;
  const btn = (v, label) => (
    <button type="button" data-testid={`${testId}-${v ? "yes" : "no"}`} onClick={() => onChange(v)} disabled={required && !v}
      className={`h-8 px-4 rounded-full text-xs font-bold border transition-colors ${value === v ? (v ? "bg-emerald-500 text-zinc-950 border-emerald-500" : "bg-red-500 text-white border-red-500") : "border-zinc-700 text-zinc-300 hover:border-zinc-500"} ${required && !v ? "opacity-30 cursor-not-allowed" : ""}`}>{label}</button>
  );
  return (
    <div className={`p-3 rounded-lg border flex flex-col sm:flex-row sm:items-center gap-2 ${answered ? "border-zinc-800 bg-zinc-950" : "border-amber-500/40 bg-amber-500/5"}`} data-testid={`${testId}-row`}>
      <div className="flex-1 text-xs text-zinc-300"><b className={required ? "text-amber-200" : "text-zinc-100"}>{title}</b> — {desc}</div>
      <div className="flex gap-2 shrink-0">{btn(true, "SI")}{btn(false, "NO")}</div>
    </div>
  );
}
